/**
 * Date Formatting Utilities
 * 
 * Provides consistent date formatting across all pages,
 * dynamically extracting dates from event data.
 */

/**
 * Formats a date string from event data to display format
 * Input: "Monday, 24th November 2025" 
 * Output: "24th Nov 2025"
 */
export const formatDateForHero = (dateString) => {
  if (!dateString) return '';
  
  // Remove day name (e.g., "Monday, ") and keep rest
  const withoutDay = dateString.replace(/^[A-Za-z]+,\s/, '');
  
  // Replace "November" with "Nov" (and other months as needed)
  return withoutDay
    .replace('November', 'Nov')
    .replace('December', 'Dec')
    .replace('January', 'Jan')
    .replace('February', 'Feb')
    .replace('March', 'Mar')
    .replace('April', 'Apr')
    .replace('June', 'Jun')
    .replace('July', 'Jul')
    .replace('August', 'Aug')
    .replace('September', 'Sep')
    .replace('October', 'Oct');
};

/**
 * Generate hero date string from filtered events
 * Returns format like "24th & 25th Nov 2025"
 */
export const generateHeroDateFromEvents = (events) => {
  if (!events || events.length === 0) return 'Loading...';
  
  // Get unique dates from events
  const uniqueDates = [...new Set(events.map(event => event.date))];
  
  // Sort dates chronologically
  const sortedDates = uniqueDates.sort((a, b) => new Date(a) - new Date(b));
  
  if (sortedDates.length === 1) {
    return formatDateForHero(sortedDates[0]);
  }
  
  // For multiple dates, extract day numbers and use single month/year
  const dayNumbers = sortedDates.map(dateString => {
    // Extract just the day number with suffix (e.g., "24th")
    const withoutDay = dateString.replace(/^[A-Za-z]+,\s/, ''); // Remove "Monday, "
    const dayMatch = withoutDay.match(/(\d+(?:st|nd|rd|th))/);
    return dayMatch ? dayMatch[1] : '';
  });
  
  // Get month and year from the last date (assuming all dates are in same month/year)
  const lastDate = formatDateForHero(sortedDates[sortedDates.length - 1]);
  const monthYearMatch = lastDate.match(/\s(.+)$/); // Everything after the day number
  const monthYear = monthYearMatch ? monthYearMatch[1] : '';
  
  // Join day numbers with appropriate connectors, then add month/year
  let daysPart;
  if (dayNumbers.length === 2) {
    daysPart = `${dayNumbers[0]} & ${dayNumbers[1]}`;
  } else {
    daysPart = `${dayNumbers.slice(0, -1).join(', ')} & ${dayNumbers[dayNumbers.length - 1]}`;
  }
  
  return `${daysPart} ${monthYear}`;
};